import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { EventShiftsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { EventContactFilters, ShiftFilters, ShiftOrder, SortOrder } from '@/types'
import { format } from 'date-fns'
import { formatCurrency } from '@/helpers/formatCurrency'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { EditDialog } from './edit_dialog'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  createable?: boolean
  updateable?: boolean
  filters?: ShiftFilters
}

export const Table = ({ pageSize = 10, filters, createable = false, updateable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<EventShiftsQuery>(
    Query,
    { variables: { filters: filters || {}, first: pageSize } }
  )
  const { pageInfo, openNew, setOpenNew, openEdit, setOpenEdit } = useTable({ pageInfo: data?.shifts.pageInfo })
  const [shiftId, setShiftId] = useState<string>()

  const editShift = (id: string) => {
    if (!updateable) return

    setShiftId(id)
    setOpenEdit(true)
  }

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  const shiftRoles = [...new Set(data.shifts.nodes.map(s => s.shiftRole?.name || '').filter(n => n))]

  return (
    <>
      <BaseTable<EventContactFilters, ShiftOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ start: SortOrder.Desc }}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.shifts.nodes = dedupe([
              ...previousData.shifts.nodes,
              ...fetchMoreResult.shifts.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, userDisplayNameCont: value })),
            type: 'text',
            placeholder: 'Who\'s working?'
          },
          {
            index: 2,
            setValue: (set, value) => set(f => ({ ...f, shiftRoleNameEq: value })),
            type: 'select',
            options: shiftRoles.map(sr => ({ value: sr, display: sr }))
          }
        ]}
        headings={[
          { name: 'Name' },
          { name: 'Event' },
          { name: 'Role' },
          { name: 'Start', sortField: 'start', defaultDirection: SortOrder.Desc },
          { name: 'Finish', sortField: 'finish', defaultDirection: SortOrder.Desc },
          { name: 'Rate' },
        ]}
        rows={data.shifts.nodes.map(shift => (
          {
            id: shift.id,
            columns: [
              shift.user?.displayName,
              shift.event.description,
              shift.shiftRole?.name,
              format(shift.start, 'KK:mm dd/MM/yyyy'),
              format(shift.finish, 'KK:mm dd/MM/yyyy'),
              formatCurrency(shift.event.currency, shift.rateCents)
            ]
          }
        ))}
        onRowClick={editShift}
      />
      { shiftId && <EditDialog id={shiftId} open={openEdit} setOpen={setOpenEdit} /> }
      <NewDialog
        defaultValues={{ eventId: filters?.eventIdEq || undefined, userId: filters?.userIdEq }}
        open={openNew}
        setOpen={setOpenNew}
      />
    </>
  )
}
